import { useCallback, useMemo } from 'react';

import { type Period } from '@/modules/analytics/types';
import { getPeriodDates } from '@/modules/analytics/utils';
import { addDaysToDate, getDaysDifference } from '@/util/dateUtils';

export const useRetentionBounds = (logsLimit: number | undefined) => {
  const hasLimit = typeof logsLimit === 'number' && logsLimit > 0;
  const midnight = new Date().setHours(0, 0, 0, 0);

  const bounds = useMemo(() => {
    const today = new Date(midnight);
    return { today, earliest: hasLimit ? addDaysToDate(today, -logsLimit) : null };
  }, [hasLimit, logsLimit, midnight]);

  const isDayDisabled = useCallback(
    (day: Date) => day > bounds.today || (!!bounds.earliest && day < bounds.earliest),
    [bounds]
  );

  const isPresetDisabled = useCallback(
    (period: Period) => {
      if (!hasLimit) {
        return false;
      }
      const resolved = getPeriodDates('', '', period);
      const daysBack = getDaysDifference(new Date(), resolved.startDate);
      return !!daysBack && daysBack + 1 > logsLimit;
    },
    [hasLimit, logsLimit]
  );

  return { isDayDisabled, isPresetDisabled, earliest: bounds.earliest, today: bounds.today };
};
